import { DialogModule } from '@angular/cdk/dialog';
import { LayoutModule } from '@angular/cdk/layout';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideCheckNoChangesConfig,
  provideZoneChangeDetection,
  type ApplicationConfig,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NoPreloading,
  provideRouter,
  Router,
  RouteReuseStrategy,
  withComponentInputBinding,
  withDebugTracing,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, type Translation } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { EffectsModule } from '@ngrx/effects';
import {
  NavigationActionTiming,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { NgxPermissionsModule } from 'ngx-permissions';
import { concatMap, first, type Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthModule } from './auth/auth.module';
import { authFeatureFlags } from './auth/consts/auth.consts';
import { AuthTokenRefreshInterceptor } from './auth/interceptors/auth-token-refresh.interceptor';
import { CheckAuthorizationStatusInterceptor } from './auth/interceptors/check-authorization-status.interceptor';
import { AuthSessionService } from './auth/services/auth-session.service';
import { ALL_AVAILABLE_LANGUAGES } from './core/consts/all-available-languages.consts';
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_CODE_TO_LOCALE_MAPPING,
} from './core/consts/languages.consts';
import { ROUTES } from './core/core.routes';
import { APP_QUERY_CLIENT } from './core/helpers/tanstack-create-query-client.helper';
import type { CountryDto } from './core/models/countries.dto.models';
import type { LanguageDto } from './core/models/languages.dto.models';
import { CountriesService } from './core/services/countries.service';
import { CustomRouteReuseStrategy } from './core/services/custom-route-reuse-strategy.service';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { LanguagesService } from './core/services/languages.service';
import { SentryService } from './core/services/sentry.service';
import { TranslocoHttpLoader } from './core/services/transloco-http-loader.service';
import { EFFECTS, META_REDUCERS, reducers } from './core/store';

// TODO: implement an interceptor to abort GET requests when the user navigates away from the page

const configuration = {
  shouldUseFallbackTranslation:
    !environment.isLocal && !environment.isDev && !environment.isQa,
  router: {
    shouldEnableDebugTracing: false,
    shouldEnableViewTransitions: false,
  },
  zoneless: {
    shouldPerformChecksForCompatibility: {
      // NOTE: we should enable this from time to time to check for compatibility issues
      periodically: false,
    },
  },
};

export const applicationConfiguration: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({
      eventCoalescing: true,
      runCoalescing: true,
    }),
    provideRouter(
      ROUTES,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      ...(configuration.router.shouldEnableDebugTracing ?
        [withDebugTracing()]
      : []),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
      withPreloading(NoPreloading),
      ...(configuration.router.shouldEnableViewTransitions ?
        [withViewTransitions()]
      : []),
      withComponentInputBinding(),
    ),
    ...((
      configuration.zoneless.shouldPerformChecksForCompatibility.periodically
    ) ?
      [
        provideCheckNoChangesConfig({
          exhaustive: true,
          interval:
            (
              configuration.zoneless.shouldPerformChecksForCompatibility
                .periodically
            ) ?
              1000
            : undefined,
        }),
      ]
    : []),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      AuthModule,
      LayoutModule,
      NgxPermissionsModule.forRoot(),
      DialogModule,
      StoreRouterConnectingModule.forRoot({
        navigationActionTiming: NavigationActionTiming.PostActivation,
      }),
      StoreModule.forRoot(reducers, {
        metaReducers: META_REDUCERS,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictActionWithinNgZone: true,
          strictActionTypeUniqueness: true,
        },
      }),
      EffectsModule.forRoot(EFFECTS),
      environment.production ?
        []
      : StoreDevtoolsModule.instrument({
          autoPause: true,
        }),
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideTanStackQuery(
      APP_QUERY_CLIENT,
      withDevtools(() => ({ loadDevtools: environment.useTanStackDevTools })),
    ),
    // TODO: translations (send error to Slack when a translation is missing?)
    provideTransloco({
      config: {
        availableLangs: ALL_AVAILABLE_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        fallbackLang: DEFAULT_LANGUAGE,
        missingHandler: {
          // NOTE: it will use the first language set in the `fallbackLang` property
          useFallbackTranslation: configuration.shouldUseFallbackTranslation,
        },
        // TODO: enable when we get rid of the `LanguageReloadModalComponent`
        reRenderOnLangChange: false,
        prodMode: environment.production,
        flatten: {
          aot: environment.production,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      locales: AVAILABLE_LANGUAGES,
    }),
    provideTranslocoLocale({
      langToLocaleMapping: LANGUAGE_CODE_TO_LOCALE_MAPPING,
    }),
    ...(authFeatureFlags.isAccessTokenRefreshEnabled ?
      [
        provideAppInitializer((): Observable<boolean> => {
          return inject(AuthSessionService).restoreSessionIfNeeded();
        }),
      ]
    : []),
    provideAppInitializer((): Observable<CountryDto[]> => {
      return inject(CountriesService).initializeCountries();
    }),
    provideAppInitializer((): Observable<LanguageDto[]> => {
      return inject(LanguagesService).initializeLanguages();
    }),
    provideAppInitializer((): Observable<Translation> => {
      const languagesService = inject(LanguagesService);

      return languagesService.isLanguageSelectorAvailable$.pipe(
        first(),
        concatMap((isLanguageSelectorAvailable) => {
          const activeLanguage =
            (isLanguageSelectorAvailable ?
              languagesService.detectActiveLanguageFromLocalStorageOrBrowser()
            : undefined) ?? DEFAULT_LANGUAGE;

          languagesService.setActiveLanguage(activeLanguage);

          return languagesService.loadLanguage(activeLanguage);
        }),
      );
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass:
        authFeatureFlags.isAccessTokenRefreshEnabled ?
          AuthTokenRefreshInterceptor
        : CheckAuthorizationStatusInterceptor,
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production && !environment.isInCypress,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    // NOTE: Keep last - prevents override
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    /**
     * NOTE: During the Angular 20 to 21 update, Angular Material switched to the Popover API by default.
     * This causes conflicts with our custom implementations (e.g., OverlayService, CustomTitleDirective)
     * and leads to E2E and component test failures. Setting `usePopover: false` is a temporary
     * solution to maintain compatibility without rewriting our application code.
     *
     * TODO: Consider refactoring our implementations to support the Popover API and remove this override.
     */
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    ...(environment.useSentry ?
      [
        provideAppInitializer(() => {
          inject(SentryService);
        }),
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        provideAppInitializer(() => {
          inject(Sentry.TraceService);
        }),
      ]
    : []),
  ],
};
